import { ReactNode, useMemo } from "react";
import { Line } from "react-chartjs-2";
import "./chartSetup";
import DateRangePicker, { DateRange } from "../common/DateRangePicker";
import { ChartSkeleton } from "../common/Skeleton";
import WidgetErrorState from "../common/WidgetErrorState";
import EmptyState from "../common/EmptyState";
import StaleDataBanner from "../common/StaleDataBanner";
import { BarChart2 } from "lucide-react";

export interface LineChartPoint {
  value: number;
  /** Period label shown in the tooltip, e.g. "Sep 8 – Sep 14". */
  label?: string;
}

export type GranularityOption = "month" | "week" | "day" | "auto";

export interface ChartFilter {
  preset?: string;
  startDate?: string;
  endDate?: string;
  granularity?: GranularityOption;
}

export interface OnboardingLineChartProps {
  title: string;
  icon: ReactNode;
  points: LineChartPoint[];
  xLabels?: string[];
  color?: string;
  startDate?: string;
  endDate?: string;
  onDateRangeChange?: (range: DateRange) => void;
  filter?: ChartFilter;
  onFilterChange?: (newFilter: ChartFilter) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  isStale?: boolean;
  onResetDateRange?: () => void;
  emptyActionTo?: string;
  emptyActionLabel?: string;
}

const GRID_COLOR = "#E4EAF0";
const TICK_COLOR = "#8A929B";
const TOOLTIP_BG_COLOR = "#16181D";
const TOOLTIP_TITLE_COLOR = "#fff";
const TOOLTIP_BODY_COLOR = "rgba(255,255,255,0.85)";

const toShortTick = (full: string): string => {
  const dash = full.indexOf(" – ");
  if (dash !== -1) return full.slice(0, dash);
  const quote = full.lastIndexOf("'");
  if (quote > 0 && full[quote - 1] === " ") return full.slice(0, quote - 1);
  return full;
};

const OnboardingLineChart = ({
  title,
  icon,
  points,
  color = "#15BBAE",
  startDate,
  endDate,
  onDateRangeChange,
  filter,
  onFilterChange,
  loading = false,
  error = null,
  onRetry,
  isStale = false,
  onResetDateRange,
  emptyActionTo,
  emptyActionLabel,
}: OnboardingLineChartProps) => {
  const effectiveStart = startDate ?? filter?.startDate ?? "";
  const effectiveEnd = endDate ?? filter?.endDate ?? "";

  const handleRangeChange = (range: DateRange) => {
    if (onDateRangeChange) {
      onDateRangeChange(range);
    }
    if (onFilterChange) {
      onFilterChange({
        preset: "custom",
        startDate: range.startDate,
        endDate: range.endDate,
        granularity: filter?.granularity,
      });
    }
  };

  const data = useMemo(
    () => ({
      labels: points.map((p) => p.label ?? ""),
      datasets: [
        {
          data: points.map((p) => p.value),
          borderColor: color,
          backgroundColor: color,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 3,
          pointBackgroundColor: color,
          fill: false,
        },
      ],
    }),
    [points, color]
  );

  const options: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: TOOLTIP_BG_COLOR,
          padding: 10,
          titleColor: TOOLTIP_TITLE_COLOR,
          bodyColor: TOOLTIP_BODY_COLOR,
          displayColors: false,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: TICK_COLOR,
            maxRotation: 0,
            callback: (_value: any, index: number) => {
              const full = points[index]?.label ?? "";
              return full ? toShortTick(full) : "";
            },
          },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: TICK_COLOR },
          grid: { color: GRID_COLOR },
        },
      },
    }),
    [points]
  );

  // Initial loading state with no prior data
  if (loading && points.length === 0 && !error) {
    return <ChartSkeleton height={220} />;
  }

  // Hard failure with no prior data
  if (error && points.length === 0) {
    return (
      <div className="space-y-3 rounded-lg border border-[#E4EAF0] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="flex items-center gap-1.5 text-[15px] font-semibold text-[#16181D]">
            <span aria-hidden="true" className="flex items-center text-primary-500">
              {icon}
            </span>
            {title}
          </h3>
        </div>
        <WidgetErrorState
          title={`${title} Unavailable`}
          message={error}
          onRetry={onRetry}
          variant="card"
          className="border-0 shadow-none py-6"
        />
      </div>
    );
  }

  const isAllZero = points.length === 0 || points.every((p) => p.value === 0);

  return (
    <div className="space-y-3 rounded-lg border border-[#E4EAF0] bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="flex items-center gap-1.5 text-[15px] font-semibold text-[#16181D]">
          <span aria-hidden="true" className="flex items-center text-primary-500">
            {icon}
          </span>
          {title}
        </h3>

        {/* Date Range Picker */}
        <DateRangePicker
          startDate={effectiveStart}
          endDate={effectiveEnd}
          onChange={handleRangeChange}
          disabled={loading}
        />
      </div>

      {/* Stale data indication when refresh failed */}
      {isStale && (
        <StaleDataBanner
          message="Failed to update chart for the selected range. Showing previous trend data."
          onRetry={onRetry}
        />
      )}

      {/* Empty State vs Chart Canvas */}
      {isAllZero && !loading ? (
        <div className="relative h-[220px] w-full min-w-0 flex items-center justify-center">
          <EmptyState
            icon={<BarChart2 size={24} />}
            title="No onboarding activity in this period"
            description="No registrations were recorded for the selected date range. Try broadening the date range."
            action={
              onResetDateRange
                ? {
                    label: "Reset Date Range",
                    onClick: onResetDateRange,
                  }
                : emptyActionTo && emptyActionLabel
                ? {
                    label: emptyActionLabel,
                    to: emptyActionTo,
                  }
                : undefined
            }
            secondaryAction={
              onResetDateRange && emptyActionTo && emptyActionLabel
                ? {
                    label: emptyActionLabel,
                    to: emptyActionTo,
                  }
                : undefined
            }
            variant="chart"
          />
        </div>
      ) : (
        <div className="relative h-[220px] w-full min-w-0">
          <Line data={data} options={options} />
          {loading && (
            <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/40 backdrop-blur-[1px] transition-all">
              <div className="mt-3 flex items-center gap-2 rounded-full border border-[#E4EAF0] bg-white/95 px-3 py-1 text-xs font-semibold text-[#63788E] shadow-sm">
                <div className="h-3 w-3 animate-spin rounded-full border border-primary-600 border-t-transparent" />
                Updating...
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingLineChart;
